import { t } from "i18next";
import * as z from "zod";

import {
  compactIdentifier,
  isValidPhoneE164,
  isValidRoutableEmail,
  normalizeEmail,
  normalizePhone,
} from "@/core/utils/func/input-normalization";
import {
  KYC_DOCUMENT_TYPES,
  PARTY_TYPES,
  PREFERRED_LANGUAGES,
} from "@/features/shared/api/enums";

/**
 * Schéma du formulaire d'identité (création/édition d'une Party) — étape 2
 * du parcours d'inscription.
 *
 * Factory (règle projet) : créé via `createPartySchema()` et mémoïsé au
 * niveau composant sur la langue courante — jamais d'instance exportée.
 * Les valeurs d'énumérations viennent de l'annexe A (features/shared).
 */
export const createPartySchema = () =>
  z.object({
    partyType: z.enum(PARTY_TYPES, {
      errorMap: () => ({ message: t("requiredFieldError") }),
    }),
    firstName: z.string().trim().min(1, { message: t("requiredFieldError") }),
    lastName: z.string().trim().min(1, { message: t("requiredFieldError") }),
    dateOfBirth: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, { message: t("invalidFieldError") })
      .or(z.literal("")),
    nationality: z.string().trim().max(2).or(z.literal("")),
    phone: z
      .string()
      .transform((raw) => {
        const trimmed = raw.trim();
        return trimmed ? normalizePhone(trimmed) : "";
      })
      .refine((value) => value === "" || isValidPhoneE164(value), {
        message: t("invalidPhoneNumberError"),
      }),
    email: z
      .string()
      .transform((raw) => {
        const trimmed = raw.trim();
        return trimmed ? normalizeEmail(trimmed) : "";
      })
      .refine((value) => value === "" || isValidRoutableEmail(value), {
        message: t("invalidEmailError"),
      }),
    nationalIdType: z.enum(KYC_DOCUMENT_TYPES).optional(),
    nationalIdNumber: z
      .string()
      .transform((raw) => {
        const trimmed = raw.trim();
        return trimmed ? compactIdentifier(trimmed) : "";
      }),
    preferredLanguage: z.enum(PREFERRED_LANGUAGES).optional(),
    // Obligatoire depuis le guide v1.3 (compte Keycloak créé à l'étape 1 du
    // parcours ou fourni par un administrateur).
    keycloakUserId: z
      .string()
      .trim()
      .min(1, { message: t("requiredFieldError") })
      .uuid({ message: t("invalidFieldError") }),
    // Delta v3.4 : jeton de preuve de possession du numéro, obtenu via
    // Otp/verify canal SMS. Jamais saisi, pré-rempli par le composant
    // OtpChallenge quand l'opérateur déclenche la vérification du téléphone.
    phoneVerificationToken: z.string().trim().optional(),
  });

export type PartyFormValues = z.infer<ReturnType<typeof createPartySchema>>;
